import { useCallback, useEffect, useReducer, useRef } from "react";
import { DocumentMigrationErrorCode } from "@common/constants/accounting-migration";
import type { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";
import { GlBasis } from "@common/schemas/accounting-integration.schema";
import {
    DocumentMigrationError,
    documentMigrationErrorToastMessage,
    extractMigrationFileById,
    MIGRATION_EXTRACTION_FAILED_TOAST,
    setGlExtractionBasis,
} from "@api/accountingIntegration.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type {
    MigrationDoc,
    MigrationStatusText,
} from "./migrationFileMatching.types";
import {
    docsReducer,
    getDocRestoration,
    migrationDocsToHydrate,
    normalizedExtractionWarnings,
    rejectionStatusText,
    type DocAction,
    type MigrationRestoreDocument,
} from "./migrationFileRows.reducer";
import {
    formatCoaSummary,
    formatGlSummary,
    unrecognizedMessageForSource,
} from "./migrationFileUploadFormatting";
import { documentIdsKey } from "./migrationRestoreDocuments";

const EXTRACTION_FAILED_MESSAGE: MigrationStatusText = {
    short: "Extraction failed",
    tooltip: "Couldn't read this file",
};

interface UseMigrationFileRowsParams {
    workspaceId: string;
    entityId: number;
    source: CoADocumentMigrationSource;
    /**
     * Linked documents from task detail; `undefined` while detail hasn't
     * loaded yet (the task list endpoint omits `financialDocuments`).
     */
    restoreDocuments?: MigrationRestoreDocument[];
    onLinkDocument: (documentId: number) => Promise<void>;
    onUnlinkDocuments: (documentIds: number[]) => Promise<void>;
}

export function useMigrationFileRows(params: UseMigrationFileRowsParams) {
    const {
        workspaceId,
        entityId,
        source,
        restoreDocuments,
        onLinkDocument,
        onUnlinkDocuments,
    } = params;

    const unrecognizedMessage = unrecognizedMessageForSource(source);
    const { error: toastError } = useToaster();

    const [docs, dispatch] = useReducer(docsReducer, []);
    const restoredDocumentIdsKeyRef = useRef<string | null>(null);
    const restoreExtractGenerationRef = useRef(0);
    const isMountedRef = useRef(true);

    const docsRef = useRef<MigrationDoc[]>([]);

    const applyAction = useCallback((action: DocAction) => {
        docsRef.current = docsReducer(docsRef.current, action);
        dispatch(action);
    }, []);

    const extractLinkedDocument = useCallback(
        async (documentId: number, isCancelled?: () => boolean) => {
            try {
                const data = await extractMigrationFileById({
                    workspaceId,
                    entityId,
                    source,
                    documentId,
                });
                if (isCancelled?.()) {
                    return;
                }
                const isCoa = data.fileType === "coa";
                const count = isCoa
                    ? data.accountsCount
                    : (data.entriesCount ?? data.journalEntriesCount);
                let summary = "Extraction complete";
                if (count != null) {
                    summary = isCoa
                        ? formatCoaSummary(count)
                        : formatGlSummary(count);
                }
                applyAction({
                    type: "EXTRACTION_SUCCESS",
                    documentId,
                    role: data.fileType,
                    period: data.period ?? null,
                    summary,
                    warnings: normalizedExtractionWarnings(data.warnings),
                });
            } catch (error) {
                if (isCancelled?.()) {
                    return;
                }
                const detail =
                    error instanceof DocumentMigrationError
                        ? error.detail
                        : null;
                const rejection =
                    detail?.code === DocumentMigrationErrorCode.IMPORT_REJECTED
                        ? detail.rejection
                        : undefined;
                let statusMessage = EXTRACTION_FAILED_MESSAGE;
                if (rejection) {
                    statusMessage = rejectionStatusText(rejection);
                } else if (
                    detail?.code === DocumentMigrationErrorCode.PARSE_FAILED
                ) {
                    statusMessage = unrecognizedMessage;
                }
                applyAction({
                    type: "MARK_UNVERIFIED",
                    documentId,
                    statusMessage,
                    rejection,
                });
                if (!isCancelled) {
                    toastError(
                        documentMigrationErrorToastMessage(
                            error,
                            MIGRATION_EXTRACTION_FAILED_TOAST,
                        ),
                    );
                }
            }
        },
        [
            workspaceId,
            entityId,
            source,
            applyAction,
            unrecognizedMessage,
            toastError,
        ],
    );

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const restoredDocumentIdsKey =
        restoreDocuments === undefined
            ? null
            : documentIdsKey(restoreDocuments.map((document) => document.id));

    useEffect(() => {
        if (restoreDocuments === undefined) {
            return;
        }
        if (restoredDocumentIdsKeyRef.current === restoredDocumentIdsKey) {
            return;
        }
        restoredDocumentIdsKeyRef.current = restoredDocumentIdsKey;
        const restoredDocs = restoreDocuments.map((document) =>
            getDocRestoration(document, source),
        );
        const docsToHydrate = migrationDocsToHydrate(
            docsRef.current,
            restoredDocs,
        );
        if (docsToHydrate.length === 0) {
            return;
        }
        applyAction({
            type: "RESTORE",
            docs: [...docsRef.current, ...docsToHydrate],
        });
        const extractingDocumentIds = docsToHydrate
            .filter((doc) => doc.status === "extracting")
            .map((doc) => doc.documentId);
        const generation = ++restoreExtractGenerationRef.current;
        const isRestoreExtractCancelled = () =>
            !isMountedRef.current ||
            restoreExtractGenerationRef.current !== generation;
        void (async () => {
            for (const documentId of extractingDocumentIds) {
                if (isRestoreExtractCancelled()) {
                    return;
                }
                await extractLinkedDocument(
                    documentId,
                    isRestoreExtractCancelled,
                );
            }
        })();
    }, [
        restoreDocuments,
        restoredDocumentIdsKey,
        applyAction,
        source,
        extractLinkedDocument,
    ]);

    const addDocument = useCallback(
        async (documentId: number, fileName: string) => {
            applyAction({ type: "ADD_EXTRACTING", documentId, fileName });
            await extractLinkedDocument(documentId);
            try {
                await onLinkDocument(documentId);
            } catch {
                // Handler already toasted; keep the row visible for retry.
            }
        },
        [applyAction, extractLinkedDocument, onLinkDocument],
    );

    const removeDoc = useCallback(
        async (documentId: number) => {
            const removedDoc = docsRef.current.find(
                (doc) => doc.documentId === documentId,
            );
            applyAction({ type: "REMOVE", documentId });
            try {
                await onUnlinkDocuments([documentId]);
            } catch {
                if (removedDoc) {
                    applyAction({ type: "READD", doc: removedDoc });
                }
            }
        },
        [applyAction, onUnlinkDocuments],
    );

    const setDocBasis = useCallback(
        async (documentId: number, basis: GlBasis) => {
            try {
                await setGlExtractionBasis({
                    workspaceId,
                    entityId,
                    documentId,
                    basis,
                });
                applyAction({ type: "SET_BASIS", documentId, basis });
            } catch {
                // The API client surfaces the failure via toast.
            }
        },
        [workspaceId, entityId, applyAction],
    );

    const dismissParserWarning = useCallback(
        (documentId: number) => {
            applyAction({ type: "DISMISS_PARSER_WARNING", documentId });
        },
        [applyAction],
    );

    const isAnyExtracting = docs.some((doc) => doc.status === "extracting");

    return {
        docs,
        addDocument,
        removeDoc,
        setDocBasis,
        dismissParserWarning,
        isAnyExtracting,
    };
}
